import React from 'react';
import { ICreative } from 'src/lib/schemas/campaign/newFlowCampaign';
import MessageCreative from '../MessageCreative';
import TwitterCreative from '../TwitterCreative';
import SquareCreative from '../meta/SquareCreative';
import GoogleSquareCreative from '../google-display/SquareCreative';
import StoryCreative from '../meta/StoryCreative';
import GoogleCreative from '../GoogleCreative';
import EmailCreative from '../EmailCreative';
import { Flex } from 'src/components/ui';
import { IChannelCreativeAttributes } from 'src/lib/schemas';
import { DDPhotoEditorProps } from '../../design-directions/DDPhotoEditor';
import CustomCreative from '../CustomCreative';
import HorizontalCreative from '../google-display/HorizontalCreative';
import VideoSquareCreative from '../meta/VideoSquareCreative';
import VideoStoryCreative from '../meta/VideoStoryCreative';
import GoogleVideoSquareCreative from '../google-display/VideoSquareCreative';

const placementComponentMap: {
	[key: string]: React.ComponentType<{
		creative: ICreative;
		withActions?: boolean;
		size?: string;
		selectedHeadline?: string;
		selectedDescription?: string;
		creativesConfig?: IChannelCreativeAttributes[];
		editMode?: boolean;
		isPaid?: boolean;
	}>;
} = {
	smsDirectMarketing: MessageCreative,
	twitterSocialMedia: TwitterCreative,
	instagramFeedPaidAd: SquareCreative,
	instagramStoryPaidAd: StoryCreative,
	instagramFeedSocialMedia: SquareCreative,
	instagramStorySocialMedia: StoryCreative,
	facebookFeedSocialMedia: SquareCreative,
	facebookStorySocialMedia: StoryCreative,
	facebookFeedPaidAd: SquareCreative,
	facebookStoryPaidAd: StoryCreative,
	googleSearch: GoogleCreative,
	googleDISPLAY_MAIN_PMAX: GoogleSquareCreative,
	googleDISPLAY_HORIZ_PMAX: HorizontalCreative,
	googleDISPLAY_MAIN: GoogleSquareCreative,
	googleDISPLAY_HORIZ: HorizontalCreative,
	google: GoogleCreative,
	emailDirectMarketing: EmailCreative,
	a6: CustomCreative,
	facebookVideoPaidAd: VideoSquareCreative,
	facebookStoryVideoPaidAd: VideoStoryCreative,
	instagramVideoPaidAd: VideoSquareCreative,
	instagramStoryVideoPaidAd: VideoStoryCreative,
	googleVIDEO_PMAX: GoogleVideoSquareCreative,
	facebookVideoSocialMedia: VideoSquareCreative,
	facebookStoryVideoSocialMedia: VideoStoryCreative,
	instagramVideoSocialMedia: VideoSquareCreative,
	instagramStoryVideoSocialMedia: VideoStoryCreative,
};

interface RenderCreativesByPlacementProps extends Partial<DDPhotoEditorProps> {
	creatives: ICreative[];
	withActions?: boolean;
	size?: string;
	selectedHeadline?: string;
	selectedDescription?: string;
	creativesConfig?: IChannelCreativeAttributes[];
	editMode?: boolean;
}
export const renderCreativesByPlacement = ({
	creatives,
	withActions = true,
	size = 'sm',
	selectedHeadline,
	selectedDescription,
	creativesConfig,
	editMode = false,
	...photoEditorProps
}: RenderCreativesByPlacementProps) => {
	const renderedComponents: React.ReactNode[] = [];
	const seenComponentTypes = new Set<string>();

	creatives.forEach((creative) => {
		creative.placements?.forEach((placement) => {
			const isPaid = placement.includes('Paid');
			const Component = placementComponentMap[placement];
			const componentTypeName = Component?.name;

			if (Component && !seenComponentTypes.has(componentTypeName)) {
				renderedComponents.push(
					<Component
						key={placement + creative.id}
						creative={creative}
						withActions={withActions}
						size={size}
						selectedHeadline={selectedHeadline}
						selectedDescription={selectedDescription}
						creativesConfig={creativesConfig}
						editMode={editMode}
						isPaid={isPaid}
						{...photoEditorProps}
					/>,
				);
				seenComponentTypes.add(componentTypeName);
			}
		});
	});

	return (
		<Flex
			gap={{ base: 2, md: 4 }}
			overflowX="auto"
			pb={2}
			pr={{ base: 4, md: 0 }}
			align="flex-start"
			sx={{
				'&::-webkit-scrollbar': { height: '6px' },
				'&::-webkit-scrollbar-thumb': { bg: 'gray.300', borderRadius: 'full' },
				'&::after': {
					content: '""',
					flexShrink: 0,
					width: '1px',
				},
			}}
		>
			{renderedComponents}
		</Flex>
	);
};
